import { type TFunction } from 'i18next'
import { jsPDF } from 'jspdf'
import { type Params } from 'react-router'
import { type TrainingProgramCompletion } from '#prisma/generated/client.ts'
import { getLocalizedContent, type LocalizedContent } from './i18n.ts'
import englishCertificateImage from './img/sparkling-blank-certificate-english.png'
import spanishCertificateImage from './img/sparkling-blank-certificate-spanish.png'
import { addFonts, fontSizeInPt } from './pdf/pdf.ts'

export function checkForTrainingMode(params: Params) {
  return params.unitSlug !== undefined
}

interface User {
  firstName: string
  lastName: string
}

const COLORS = {
  purple7: '#402060',
}

export const downloadProgramCertificate = async (
  user: User,
  t: TFunction,
  certificateLanguageAndAgeTrack: LocalizedContent,
  locale: string,
  trainingProgramCompletion: TrainingProgramCompletion,
) => {
  const certificateSize = {
    width: 1153, // 865 pt
    height: 879, // 659 pt,
  }
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'px',
    format: [certificateSize.width, certificateSize.height],
    hotfixes: ['px_scaling'],
  })
  addFonts(doc)

  let certificateImage = englishCertificateImage

  if (locale === 'es') {
    certificateImage = spanishCertificateImage
  }

  doc.addImage(
    certificateImage,
    'PNG',
    0,
    0,
    certificateSize.width,
    certificateSize.height,
    undefined,
    'NONE',
  )

  const userFullName = `${user.firstName} ${user.lastName}`

  const date = trainingProgramCompletion.createdAt

  const yUserFullNamePosition = 513
  const yCertifiedTutorPosition = 580
  const yCertificationPosition = 622
  const yCertificatePresentedPosition = 647

  doc
    .setFontSize(fontSizeInPt(7))
    .setTextColor(COLORS.purple7)
    .setFont('Poppins', 'bold')
    .text(
      `${user.firstName} ${user.lastName}`,
      certificateSize.width / 2,
      yUserFullNamePosition,
      { align: 'center' },
    )

  doc
    .setFontSize(fontSizeInPt(5))
    .setTextColor(COLORS.purple7)
    .setFont('Poppins', 'normal')
    .text(
      t('trainingCompletion.certifiedTutor'),
      certificateSize.width / 2,
      yCertifiedTutorPosition,
      { align: 'center' },
    )

  doc
    .setFontSize(fontSizeInPt(4))
    .setTextColor(COLORS.purple7)
    .setFont('Poppins', 'normal')
    .text(
      t('trainingCompletion.certification', {
        certificateLanguageAndAgeTrack: getLocalizedContent(
          certificateLanguageAndAgeTrack,
          locale,
        ),
      }),
      certificateSize.width / 2,
      yCertificationPosition,
      { align: 'center' },
    )

  doc
    .setFontSize(fontSizeInPt(4))
    .setTextColor(COLORS.purple7)
    .setFont('Poppins', 'normal')
    .text(
      t('trainingCompletion.certificatePresented').replace(
        '{{date}}',
        new Intl.DateTimeFormat(locale, {
          dateStyle: 'medium',
        }).format(date),
      ),
      certificateSize.width / 2,
      yCertificatePresentedPosition,
      { align: 'center' },
    )

  doc.save(
    `training-${userFullName
      .toLocaleLowerCase()
      .split(' ')
      .join('-')}-certificate.pdf`,
  )
}
